import React, { useState, useMemo, useEffect, useRef } from "react";
import { Creator, Voucher, CreatorTier, TicketOption } from "../../types";
import {
  getAffiliateStats,
  getVouchers,
  createVoucher,
  updateVoucher,
  deleteVoucher as apiDeleteVoucher,
  AffiliateStats,
  getVoucherTemplateUrl,
  importVouchers,
  bulkDeleteVouchers,
  PaginationMeta,
} from "../../services/api/admin/affiliates";
import {
  getPartners,
  createPartner,
  updatePartner,
  deletePartner as apiDeletePartner,
  bulkDeletePartners,
  getPartnerTemplateUrl,
  importPartners,
} from "../../services/api/admin/partners";
import { getAdminTickets } from "../../services/api/admin/tickets";
import {
  Users,
  Tag,
  Plus,
  Trash2,
  TrendingUp,
  Search,
  Wallet,
  UserPlus,
  X,
  Banknote,
  Loader2,
  Download,
  Upload,
  FileSpreadsheet,
  Eye,
  Edit3,
  CheckCheck,
  Copy,
} from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';
import Pagination from "../Pagination";

interface AffiliateProgramProps {
  onRefresh: () => void;
}

type TabType = "overview" | "partners" | "vouchers" | "register";

export const AffiliateProgram: React.FC<AffiliateProgramProps> = ({
  onRefresh,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Data States
  const [partners, setPartners] = useState<Creator[]>([]);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [availableTickets, setAvailableTickets] = useState<TicketOption[]>([]);
  const [allPartners, setAllPartners] = useState<Creator[]>([]);
  const [partnerSearchQuery, setPartnerSearchQuery] = useState("");
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [filteredPartners, setFilteredPartners] = useState<Creator[]>([]);
  const [copiedVouchers, setCopiedVouchers] = useState<Set<string>>(new Set());

  // Form States
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [newVoucher, setNewVoucher] = useState({
    code: "",
    type: "percent" as "percent" | "fixed",
    value: 0,
    creatorId: "",
  });

  const [isEditVoucherModalOpen, setIsEditVoucherModalOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [editVoucher, setEditVoucher] = useState({
    code: "",
    type: "percent" as "percent" | "fixed",
    value: 0,
    creatorId: "",
    partnerCommission: 6,
  });

  const [regForm, setRegForm] = useState({
    name: "",
    platformType: "instagram" as "instagram" | "tiktok" | "youtube" | "blog",
    socialMediaUrl: "",
    followersCount: "",
    whatsapp: "",
    email: "",
    bankType: "bank" as "bank" | "e-wallet",
    bankName: "",
    bankAccountNumber: "",
    bankAccountName: "",
  });

  const [selectedPartner, setSelectedPartner] = useState<Creator | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    platformType: "instagram" as "instagram" | "tiktok" | "youtube" | "blog",
    socialMediaUrl: "",
    followersCount: "",
    whatsapp: "",
    email: "",
    bankType: "bank" as "bank" | "e-wallet",
    bankName: "",
    bankAccountNumber: "",
    bankAccountName: "",
    status: "active" as "active" | "inactive",
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const formatPhoneNumber = (phone: string): string => {
    let cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = "62" + cleaned.substring(1);
    } else if (!cleaned.startsWith("62")) {
      cleaned = "62" + cleaned;
    }
    return cleaned;
  };

  const handleWhatsappChange = (value: string) => {
    let cleaned = value.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = "62" + cleaned.substring(1);
    }
    setRegForm({ ...regForm, whatsapp: cleaned });
  };

  // Refs for file inputs
  const partnerFileInputRef = useRef<HTMLInputElement>(null);
  const voucherFileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  const [partnerMeta, setPartnerMeta] = useState<PaginationMeta>({
    total: 0,
    perPage: 5,
    currentPage: 1,
    lastPage: 1,
    firstPage: 1,
    firstPageUrl: undefined,
    lastPageUrl: "",
    nextPageUrl: null,
    previousPageUrl: null,
  });
  const [partnerPage, setPartnerPage] = useState(1);

  const [voucherMeta, setVoucherMeta] = useState<PaginationMeta>({
    total: 0,
    perPage: 5,
    currentPage: 1,
    lastPage: 1,
    firstPage: 1,
    firstPageUrl: undefined,
    lastPageUrl: "",
    nextPageUrl: null,
    previousPageUrl: null,
  });
  const [voucherPage, setVoucherPage] = useState(1);
  const [voucherSearchQuery, setVoucherSearchQuery] = useState("");
  const [debouncedVoucherSearch, setDebouncedVoucherSearch] = useState("");
  const [voucherPerPage, setVoucherPerPage] = useState(5);
  const [partnerPerPage, setPartnerPerPage] = useState(5);
  const [selectedVouchers, setSelectedVouchers] = useState<string[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<string[]>([]);

  const handlePartnerPageChange = (page: number) => {
    setPartnerPage(page);
  };

  const handleVoucherPageChange = (page: number) => {
    setVoucherPage(page);
  };

  const handleVoucherPerPageChange = (perPage: number) => {
    setVoucherPerPage(perPage);
    setVoucherPage(1); // Reset to first page when changing per page
  };

  const handlePartnerPerPageChange = (perPage: number) => {
    setPartnerPerPage(perPage);
    setPartnerPage(1); // Reset to first page when changing per page
  };

  const getAllPartners = async (search = ""): Promise<Creator[]> => {
    try {
      const allData: Creator[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const res = await getPartners(page, search, 100);
        allData.push(...res.data);
        hasMore = page < res.meta.lastPage;
        page++;
      }

      return allData;
    } catch (error) {
      return [];
    }
  };

  const fetchAllPartners = async (search = "") => {
    const data = await getAllPartners(search);
    setAllPartners(data);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, partnersRes, vouchersRes, ticketsRes, allPartnersData] =
        await Promise.all([
          getAffiliateStats(),
          getPartners(1),
          getVouchers(1),
          getAdminTickets(),
          getAllPartners(), // Ambil semua partners
        ]);

      setStats(statsRes);
      setPartners(partnersRes.data);
      setPartnerMeta(partnersRes.meta);
      setVouchers(vouchersRes.data);
      setVoucherMeta(vouchersRes.meta);
      setAvailableTickets(ticketsRes);
      setAllPartners(allPartnersData);
      setSelectedVouchers([]); // Clear selected vouchers on refresh
    } catch (error) {
      console.error("Failed to fetch affiliate data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPartners = async (page = 1, search = "", perPage = 5) => {
    try {
      const res = await getPartners(page, search, perPage);
      setPartners(res.data);
      setPartnerMeta(res.meta);
    } catch (error) {
      setPartners([]);
      setPartnerMeta((prev) => ({ ...prev, total: 0 }));
    }
  };

  const fetchVouchers = async (page = 1, search = "", perPage = 5) => {
    try {
      const res = await getVouchers(page, search, perPage);
      setVouchers(res.data);
      setVoucherMeta(res.meta);
    } catch (error) {
      setVouchers([]);
      setVoucherMeta((prev) => ({ ...prev, total: 0 }));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPartnerPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce voucher search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedVoucherSearch(voucherSearchQuery);
      setVoucherPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [voucherSearchQuery]);

  useEffect(() => {
    if (activeTab === "partners") {
      fetchPartners(partnerPage, debouncedSearch, partnerPerPage);
      setSelectedPartners([]); // Clear selection when page/search changes
    }
  }, [activeTab, partnerPage, debouncedSearch, partnerPerPage]);

  useEffect(() => {
    if (activeTab === "vouchers") {
      fetchVouchers(voucherPage, debouncedVoucherSearch, voucherPerPage);
      setSelectedVouchers([]); // Clear selection when page/search changes
    }
  }, [activeTab, voucherPage, debouncedVoucherSearch, voucherPerPage]);

  // Filter partners for select
  useEffect(() => {
    if (partnerSearchQuery.trim() === "") {
      setFilteredPartners(allPartners.slice(0, 10)); // Show first 10
    } else {
      const filtered = allPartners.filter(
        (p) =>
          p.name.toLowerCase().includes(partnerSearchQuery.toLowerCase()) ||
          p.email.toLowerCase().includes(partnerSearchQuery.toLowerCase()) ||
          p.whatsapp?.includes(partnerSearchQuery)
      );
      setFilteredPartners(filtered.slice(0, 10)); // Limit to 10 results
    }
  }, [partnerSearchQuery, allPartners]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    const formattedPhone = formatPhoneNumber(regForm.whatsapp);
    if (!/^628\d{7,17}$/.test(formattedPhone)) {
      Swal.fire({
        icon: "error",
        title: "Format Nomor Salah",
        text: "Nomor WhatsApp harus dimulai dengan 08 atau 628 dan minimal 10 digit.",
      });
      return;
    }

    try {
      await createPartner({
        fullName: regForm.name,
        email: regForm.email,
        phone: formattedPhone,
        socialMediaHandle: regForm.platformType,
        socialMediaUrl: regForm.socialMediaUrl,
        followersCount: regForm.followersCount
          ? parseInt(regForm.followersCount.replace(/\D/g, ""), 10)
          : 0,
        bankType: regForm.bankType,
        bankName: regForm.bankName,
        bankAccountNumber: regForm.bankAccountNumber,
        bankAccountName: regForm.bankAccountName,
      });

      await fetchData();
      setRegForm({
        name: "",
        platformType: "instagram",
        socialMediaUrl: "",
        followersCount: "",
        whatsapp: "",
        email: "",
        bankType: "bank",
        bankName: "",
        bankAccountNumber: "",
        bankAccountName: "",
      });
      Swal.fire({
        icon: "success",
        title: "Mitra Terdaftar",
        text: "Kreator baru berhasil ditambahkan ke program.",
        timer: 2000,
        showConfirmButton: false,
      });
      setActiveTab("partners");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal Mendaftar",
        text: "Terjadi kesalahan saat mendaftarkan mitra.",
      });
    }
  };

  const handleViewPartner = (partner: Creator) => {
    setSelectedPartner(partner);
    setIsViewModalOpen(true);
  };

  const handleEditPartner = (partner: Creator) => {
    setSelectedPartner(partner);
    setEditForm({
      name: partner.name,
      platformType: (partner.platform as any) || "instagram",
      socialMediaUrl: partner.socialMediaUrl || "",
      followersCount: partner.followersCount ? partner.followersCount : "",
      whatsapp: partner.whatsapp || "",
      email: partner.email || "",
      bankType: (partner.bankType as any) || "bank",
      bankName: partner.bankName || "",
      bankAccountNumber: partner.bankAccountNumber || "",
      bankAccountName: partner.bankAccountName || "",
      status: (partner.status as any) || "active",
    });
    setIsEditModalOpen(true);
  };

  const handleEditWhatsappChange = (value: string) => {
    let cleaned = value.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = "62" + cleaned.substring(1);
    }
    setEditForm({ ...editForm, whatsapp: cleaned });
  };

  const handleUpdatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPartner) return;

    const formattedPhone = formatPhoneNumber(editForm.whatsapp);
    if (editForm.whatsapp && !/^628\d{7,17}$/.test(formattedPhone)) {
      Swal.fire({
        icon: "error",
        title: "Format Nomor Salah",
        text: "Nomor WhatsApp harus dimulai dengan 08 atau 628 dan minimal 10 digit.",
      });
      return;
    }

    setIsUpdating(true);
    try {
      await updatePartner(selectedPartner.id, {
        fullName: editForm.name,
        email: editForm.email,
        phone: formattedPhone,
        socialMediaHandle: editForm.platformType,
        socialMediaUrl: editForm.socialMediaUrl,
        followersCount: editForm.followersCount
          ? parseInt(editForm.followersCount.replace(/\D/g, ""), 10)
          : 0,
        bankType: editForm.bankType,
        bankName: editForm.bankName,
        bankAccountNumber: editForm.bankAccountNumber,
        bankAccountName: editForm.bankAccountName,
        status: editForm.status,
      });

      await fetchPartners(partnerPage, debouncedSearch, partnerPerPage);
      setIsEditModalOpen(false);
      setSelectedPartner(null);
      Swal.fire({
        icon: "success",
        title: "Berhasil Diperbarui",
        text: "Data mitra berhasil diperbarui.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal Memperbarui",
        text: "Terjadi kesalahan saat memperbarui data mitra.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCreator = async (id: string) => {
    const result = await Swal.fire({
      title: "Hapus Mitra?",
      text: "Semua voucher terkait juga akan non-aktif.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Ya, Hapus!",
    });

    if (result.isConfirmed) {
      try {
        await apiDeletePartner(id);
        await fetchData();
        Swal.fire({
          icon: "success",
          title: "Terhapus",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Gagal Menghapus" });
      }
    }
  };

  const handleAddVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVoucher.code || !newVoucher.creatorId) return;

    try {
      await createVoucher({
        partnerId: newVoucher.creatorId,
        code: newVoucher.code.toUpperCase(),
        discountType: newVoucher.type,
        discountValue: newVoucher.value,
        partnerCommission: 6,
      });

      await fetchData();
      setNewVoucher({
        code: "",
        type: "percent",
        value: 0,
        creatorId: "",
      });
      setIsVoucherModalOpen(false);
      Swal.fire({
        icon: "success",
        title: "Voucher Dibuat",
        text: "Komisi mitra otomatis 6% per transaksi",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal Membuat Voucher",
        text: "Kode mungkin sudah digunakan.",
      });
    }
  };

  const handleEditVoucher = (voucher: Voucher) => {
    setSelectedVoucher(voucher);
    setEditVoucher({
      code: voucher.code,
      type: voucher.type,
      value: voucher.value,
      creatorId: voucher.creatorId,
      partnerCommission: voucher.partnerCommission || 6,
    });
    setPartnerSearchQuery(voucher.creatorName || "");
    setIsEditVoucherModalOpen(true);
  };

  const handleUpdateVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVoucher || !editVoucher.code || !editVoucher.creatorId) return;

    try {
      await updateVoucher(selectedVoucher.id!, {
        partnerId: editVoucher.creatorId,
        code: editVoucher.code.toUpperCase(),
        discountType: editVoucher.type,
        discountValue: editVoucher.value,
        partnerCommission: editVoucher.partnerCommission,
      });

      await fetchData();
      setIsEditVoucherModalOpen(false);
      setSelectedVoucher(null);
      Swal.fire({
        icon: "success",
        title: "Voucher Diupdate",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal Update Voucher",
        text: "Kode mungkin sudah digunakan.",
      });
    }
  };

  const handleDeleteVoucher = async (id: string) => {
    const result = await Swal.fire({
      title: "Hapus Voucher?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Ya, Hapus!",
    });

    if (result.isConfirmed) {
      try {
        await apiDeleteVoucher(id);
        await fetchData();
        Swal.fire({
          icon: "success",
          title: "Terhapus",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Gagal Menghapus" });
      }
    }
  };

  const handleSelectVoucher = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedVouchers(prev => [...prev, id]);
    } else {
      setSelectedVouchers(prev => prev.filter(vId => vId !== id));
    }
  };

  const handleSelectAllVouchers = (checked: boolean) => {
    if (checked) {
      setSelectedVouchers(vouchers.map(v => v.id!));
    } else {
      setSelectedVouchers([]);
    }
  };

  const handleBulkDeleteVouchers = async () => {
    if (selectedVouchers.length === 0) return;

    const result = await Swal.fire({
      title: `Hapus ${selectedVouchers.length} Voucher?`,
      text: "Tindakan ini tidak dapat dibatalkan",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Ya, Hapus Semua!",
    });

    if (result.isConfirmed) {
      try {
        await bulkDeleteVouchers(selectedVouchers);
        setSelectedVouchers([]);
        await fetchData();
        Swal.fire({
          icon: "success",
          title: "Voucher Terhapus",
          text: `${selectedVouchers.length} voucher berhasil dihapus`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error: any) {
        Swal.fire({
          icon: "error",
          title: "Gagal Menghapus",
          text: error.message || "Terjadi kesalahan saat menghapus voucher",
        });
      }
    }
  };

  const handleExportSelectedVouchers = () => {
    if (selectedVouchers.length === 0) return;

    const selectedData = vouchers.filter(v => selectedVouchers.includes(v.id!));
    const exportData = selectedData.map(v => ({
      "Kode Voucher": v.code,
      "Tipe Diskon": v.type === "percent" ? "Persentase" : "Nominal",
      "Nilai Diskon": v.value,
      "Mitra": v.creatorName || "",
      "Komisi Mitra (%)": (v.partnerCommission || 6)
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");

    const fileName = `vouchers_selected_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Partner bulk operations
  const handleSelectPartner = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedPartners(prev => [...prev, id]);
    } else {
      setSelectedPartners(prev => prev.filter(pId => pId !== id));
    }
  };

  const handleSelectAllPartners = (checked: boolean) => {
    if (checked) {
      setSelectedPartners(partners.map(p => p.id!));
    } else {
      setSelectedPartners([]);
    }
  };

  const handleBulkDeletePartners = async () => {
    if (selectedPartners.length === 0) return;

    const result = await Swal.fire({
      title: `Hapus ${selectedPartners.length} Mitra?`,
      text: "Semua voucher terkait juga akan non-aktif. Tindakan ini tidak dapat dibatalkan",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Ya, Hapus Semua!",
    });

    if (result.isConfirmed) {
      try {
        await bulkDeletePartners(selectedPartners);
        setSelectedPartners([]);
        await fetchData();
        Swal.fire({
          icon: "success",
          title: "Mitra Terhapus",
          text: `${selectedPartners.length} mitra berhasil dihapus`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error: any) {
        Swal.fire({
          icon: "error",
          title: "Gagal Menghapus",
          text: error.message || "Terjadi kesalahan saat menghapus mitra",
        });
      }
    }
  };

  const handleExportSelectedPartners = () => {
    if (selectedPartners.length === 0) return;

    const selectedData = partners.filter(p => selectedPartners.includes(p.id!));
    const exportData = selectedData.map(p => ({
      "Nama": p.name,
      "Email": p.email,
      "WhatsApp": p.whatsapp || "",
      "Platform": p.platform,
      "URL Social Media": p.socialMediaUrl || "",
      "Followers": p.followersCount || 0,
      "Tier": p.tier || "Nano",
      "Bank Type": p.bankType,
      "Bank Name": p.bankName || "",
      "No. Rekening": p.bankAccountNumber || "",
      "Atas Nama": p.bankAccountName || "",
      "Total Komisi": p.totalCommission || 0,
      "Status": p.status || "active"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Partners");

    const fileName = `partners_selected_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Import handlers
  const handleDownloadPartnerTemplate = () => {
    const token = localStorage.getItem("auth_token");
    const url = getPartnerTemplateUrl();

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = "template_import_mitra.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch(() => {
        Swal.fire({ icon: "error", title: "Gagal mengunduh template" });
      });
  };

  const handleDownloadVoucherTemplate = () => {
    const token = localStorage.getItem("auth_token");
    const url = getVoucherTemplateUrl();

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        if (blob.size === 0) {
          throw new Error("File kosong");
        }
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = "template_import_voucher.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((err) => {
        console.error("Download error:", err);
        Swal.fire({
          icon: "error",
          title: "Gagal mengunduh template",
          text: err.message,
        });
      });
  };

  const handlePartnerFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const result = await importPartners(file);
      await fetchData();

      let message = `Berhasil: ${result.success} mitra`;
      if (result.failed > 0) {
        message += `\nGagal: ${result.failed} mitra`;
      }

      if (result.errors.length > 0) {
        Swal.fire({
          icon: result.success > 0 ? "warning" : "error",
          title: "Hasil Import Mitra",
          html: `<div class="text-left">
            <p class="mb-2">${message.replace("\n", "<br>")}</p>
            <details class="mt-4">
              <summary class="cursor-pointer text-sm text-gray-500">Lihat detail error (${
                result.errors.length
              })</summary>
              <ul class="mt-2 text-xs text-red-500 max-h-40 overflow-y-auto">
                ${result.errors.map((err) => `<li>• ${err}</li>`).join("")}
              </ul>
            </details>
          </div>`,
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Import Berhasil",
          text: message,
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Import Gagal",
        text: error.message || "Terjadi kesalahan saat import",
      });
    } finally {
      setIsImporting(false);
      if (partnerFileInputRef.current) {
        partnerFileInputRef.current.value = "";
      }
    }
  };

  const handleVoucherFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const result = await importVouchers(file);
      await fetchData();

      let message = `Berhasil: ${result.success} voucher`;
      if (result.failed > 0) {
        message += `\nGagal: ${result.failed} voucher`;
      }

      if (result.errors.length > 0) {
        Swal.fire({
          icon: result.success > 0 ? "warning" : "error",
          title: "Hasil Import Voucher",
          html: `<div class="text-left">
            <p class="mb-2">${message.replace("\n", "<br>")}</p>
            <details class="mt-4">
              <summary class="cursor-pointer text-sm text-gray-500">Lihat detail error (${
                result.errors.length
              })</summary>
              <ul class="mt-2 text-xs text-red-500 max-h-40 overflow-y-auto">
                ${result.errors.map((err) => `<li>• ${err}</li>`).join("")}
              </ul>
            </details>
          </div>`,
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Import Berhasil",
          text: message,
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Import Gagal",
        text: error.message || "Terjadi kesalahan saat import",
      });
    } finally {
      setIsImporting(false);
      if (voucherFileInputRef.current) {
        voucherFileInputRef.current.value = "";
      }
    }
  };

  const TierBadge = ({ tier }: { tier?: CreatorTier }) => {
    const styles: Record<string, string> = {
      Nano: "bg-blue-100 text-blue-700 border-blue-200",
      Micro: "bg-emerald-100 text-emerald-700 border-emerald-200",
      Macro: "bg-purple-100 text-purple-700 border-purple-200",
      Mega: "bg-rose-100 text-rose-700 border-rose-200",
      Startup: "bg-amber-100 text-amber-700 border-amber-200",
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase border tracking-widest ${
          styles[tier || "Nano"] || "bg-gray-100"
        }`}
      >
        {tier || "Nano"}
      </span>
    );
  };

  const StatsCard = ({ title, value, sub, icon: Icon, color }: any) => (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-xs font-bold uppercase tracking-wide mb-1">
          {title}
        </p>
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className={`text-xs font-medium mt-1 ${color}`}>{sub}</p>
      </div>
      <div
        className={`p-3 rounded-xl ${color
          .replace("text-", "bg-")
          .replace("600", "50")
          .replace("700", "50")}`}
      >
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
  );

  if (isLoading)
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-400">
        <Loader2 className="w-10 h-10 animate-spin mb-4" />
        <p className="font-bold font-mono uppercase tracking-[0.2em] text-xs">
          Synchronizing Affiliate Data...
        </p>
      </div>
    );

  return (
    <div className="space-y-8 animate-fadeIn pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
            Affiliate Hub
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Sistem manajemen kemitraan kreator dan distribusi komisi
            multi-tiket.
          </p>
        </div>
        <div className="flex bg-white rounded-2xl p-1 shadow-sm border border-gray-200">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
              activeTab === "overview"
                ? "bg-giz-primary text-white shadow"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("partners")}
            className={`px-4 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
              activeTab === "partners"
                ? "bg-giz-primary text-white shadow"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            Database
          </button>
          <button
            onClick={() => setActiveTab("vouchers")}
            className={`px-4 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
              activeTab === "vouchers"
                ? "bg-giz-primary text-white shadow"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            Voucher
          </button>
          <button
            onClick={() => setActiveTab("register")}
            className={`px-6 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
              activeTab === "register"
                ? "bg-indigo-600 text-white shadow"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            + Daftar
          </button>
        </div>
      </div>

      {activeTab === "overview" && (
        <div className="space-y-6 animate-slideUp">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatsCard
              title="Total Mitra"
              value={stats?.totalMitra || 0}
              sub="Nano, Micro & Macro"
              icon={Users}
              color="text-blue-600"
            />
            <StatsCard
              title="Total Komisi"
              value={`Rp ${(stats?.totalCommissionPaid || 0).toLocaleString(
                "id-ID"
              )}`}
              sub="Siap dicairkan"
              icon={Wallet}
              color="text-emerald-600"
            />
            <StatsCard
              title="Omzet Afiliasi"
              value={`Rp ${(stats?.omsetAffiliate || 0).toLocaleString(
                "id-ID"
              )}`}
              sub={`${stats?.totalVoucherUsed || 0} Transaksi`}
              icon={TrendingUp}
              color="text-orange-600"
            />
            <StatsCard
              title="Voucher Aktif"
              value={stats?.activeVouchers || 0}
              sub="Kode promo beredar"
              icon={Tag}
              color="text-purple-600"
            />
          </div>
        </div>
      )}

      {activeTab === "partners" && (
        <div className="animate-slideUp space-y-6">
          <div className="bg-white rounded-[2rem] shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50/30">
              <div className="relative w-full md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari Mitra (Nama / WA / ID)..."
                  className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-giz-primary outline-none transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Tampilkan:
                  </label>
                  <select
                    value={partnerPerPage}
                    onChange={(e) => handlePartnerPerPageChange(Number(e.target.value))}
                    className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-giz-primary outline-none font-bold"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                {/* Bulk Actions */}
                {selectedPartners.length > 0 && (
                  <>
                    <button
                      onClick={handleExportSelectedPartners}
                      className="bg-blue-50 text-blue-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-blue-100 flex items-center gap-2 transition-all"
                      title="Export data mitra terpilih ke Excel"
                    >
                      <FileSpreadsheet className="w-4 h-4" /> Export Excel ({selectedPartners.length})
                    </button>
                    <button
                      onClick={handleBulkDeletePartners}
                      className="bg-red-50 text-red-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-red-100 flex items-center gap-2 transition-all"
                      title="Hapus mitra terpilih"
                    >
                      <Trash2 className="w-4 h-4" /> Delete ({selectedPartners.length})
                    </button>
                  </>
                )}
                {/* Import Section */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadPartnerTemplate}
                    className="bg-emerald-50 text-emerald-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-emerald-100 flex items-center gap-2 transition-all"
                    title="Download Template Excel"
                  >
                    <Download className="w-4 h-4" /> Template
                  </button>
                  <input
                    type="file"
                    ref={partnerFileInputRef}
                    onChange={handlePartnerFileChange}
                    accept=".xlsx,.xls"
                    className="hidden"
                  />
                  <button
                    onClick={() => partnerFileInputRef.current?.click()}
                    disabled={isImporting}
                    className="bg-indigo-50 text-indigo-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-indigo-100 flex items-center gap-2 transition-all disabled:opacity-50"
                    title="Import dari Excel"
                  >
                    {isImporting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Import
                  </button>
                </div>
                <button
                  onClick={() => setActiveTab("register")}
                  className="bg-gray-900 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" /> Tambah
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-gray-400 uppercase text-[9px] tracking-widest border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-5">
                      <input
                        type="checkbox"
                        checked={selectedPartners.length === partners.length && partners.length > 0}
                        onChange={(e) => handleSelectAllPartners(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-6 py-5">Identitas & Tier</th>
                    <th className="px-6 py-5">WhatsApp</th>
                    <th className="px-6 py-5">Informasi Rekening</th>
                    <th className="px-6 py-5 text-right">Total Komisi</th>
                    <th className="px-6 py-5 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {partners.map((c) => {
                    return (
                      <tr
                        key={c.id}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-6 py-6">
                          <input
                            type="checkbox"
                            checked={selectedPartners.includes(c.id!)}
                            onChange={(e) => handleSelectPartner(c.id!, e.target.checked)}
                            className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-6">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-black text-gray-900 text-xs uppercase tracking-tight">
                                {c.name}
                              </span>
                              <TierBadge tier={c.tier} />
                            </div>
                            <span className="text-[11px] text-gray-500 font-bold uppercase tracking-wide">
                              {c.platform}
                            </span>
                            <span className="text-[10px] text-indigo-500 font-black uppercase tracking-wider">
                              FOLLOWERS:{" "}
                              {(c.followersCount || 0).toLocaleString("id-ID")}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-6 font-mono font-bold text-emerald-600 text-xs">
                          {c.whatsapp || "-"}
                        </td>
                        <td className="px-6 py-6">
                          <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-700 flex items-center gap-1 uppercase tracking-wider">
                              <Banknote className="w-3 h-3 text-emerald-500" />{" "}
                              {c.bankName}
                            </span>
                            <span className="text-[11px] font-mono text-slate-500 mt-0.5">
                              {c.bankAccountNumber}
                            </span>
                            <span className="text-[9px] text-slate-400 font-bold uppercase mt-0.5 tracking-widest">
                              A.N {c.bankAccountName}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-6 text-right">
                          <div className="font-black text-emerald-600 text-sm">
                            Rp{" "}
                            {(c.totalCommission || 0).toLocaleString("id-ID")}
                          </div>
                          <div className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter mt-0.5">
                            TERAKUMULASI
                          </div>
                        </td>
                        <td className="px-6 py-6 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleViewPartner(c)}
                              className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                              title="Lihat Detail"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditPartner(c)}
                              className="p-2 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCreator(c.id)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                meta={{ ...partnerMeta, currentPage: partnerPage }}
                onPageChange={handlePartnerPageChange}
                className="mb-4 p-6"
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "register" && (
        <div className="max-w-4xl mx-auto bg-white rounded-[3rem] shadow-2xl border border-gray-100 overflow-hidden animate-slideUp">
          <div className="bg-slate-900 p-10 text-white flex justify-between items-center border-b border-white/5">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-indigo-600 rounded-[1.8rem] flex items-center justify-center shadow-2xl shadow-indigo-900/50">
                <UserPlus className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-black text-2xl uppercase tracking-tighter">
                  Registrasi Mitra Baru
                </h3>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mt-2">
                  Program Afiliasi GIZ Park & RIW
                </p>
              </div>
            </div>
            <button
              onClick={() => setActiveTab("partners")}
              className="p-4 bg-white/5 hover:bg-white/10 rounded-[1.5rem] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleRegister} className="p-12 space-y-12">
            <div className="space-y-10">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                <div className="bg-indigo-50 p-2.5 rounded-xl text-indigo-600">
                  <Users className="w-5 h-5" />
                </div>
                <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.25em]">
                  Informasi Profil Kreator
                </h4>
              </div>

              <div className="grid md:grid-cols-2 gap-10">
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    NAMA LENGKAP
                  </label>
                  <input
                    required
                    type="text"
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-giz-primary focus:bg-white outline-none transition-all font-bold shadow-inner"
                    value={regForm.name}
                    onChange={(e) =>
                      setRegForm({ ...regForm, name: e.target.value })
                    }
                    placeholder="Masukkan Nama Lengkap..."
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    NOMOR WHATSAPP (AKTIF)
                  </label>
                  <input
                    required
                    type="tel"
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-giz-primary focus:bg-white outline-none transition-all font-bold shadow-inner"
                    value={regForm.whatsapp}
                    onChange={(e) => handleWhatsappChange(e.target.value)}
                    placeholder="628xxx (auto-format)"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-10">
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    EMAIL (UNTUK LOGIN)
                  </label>
                  <input
                    required
                    type="email"
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-giz-primary focus:bg-white outline-none transition-all font-bold shadow-inner"
                    value={regForm.email}
                    onChange={(e) =>
                      setRegForm({ ...regForm, email: e.target.value })
                    }
                    placeholder="nama@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    PLATFORM SOCIAL MEDIA
                  </label>
                  <select
                    required
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-white text-gray-900 focus:border-giz-primary outline-none font-black appearance-none shadow-sm cursor-pointer"
                    value={regForm.platformType}
                    onChange={(e) =>
                      setRegForm({
                        ...regForm,
                        platformType: e.target.value as any,
                      })
                    }
                  >
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="youtube">YouTube</option>
                    <option value="blog">Blog / Website</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    URL SOCIAL MEDIA
                  </label>
                  <input
                    required
                    type="url"
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-giz-primary focus:bg-white outline-none transition-all font-bold shadow-inner"
                    value={regForm.socialMediaUrl}
                    onChange={(e) =>
                      setRegForm({ ...regForm, socialMediaUrl: e.target.value })
                    }
                    placeholder={
                      regForm.platformType === "instagram"
                        ? "https://instagram.com/username"
                        : regForm.platformType === "tiktok"
                        ? "https://tiktok.com/@username"
                        : regForm.platformType === "youtube"
                        ? "https://youtube.com/@channel"
                        : "https://yourblog.com"
                    }
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-1 gap-8">
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    JUMLAH FOLLOWERS
                  </label>
                  <input
                    type="text"
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-giz-primary focus:bg-white outline-none transition-all font-bold shadow-inner"
                    value={regForm.followersCount}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      const formatted = val
                        ? parseInt(val, 10).toLocaleString("id-ID")
                        : "";
                      setRegForm({ ...regForm, followersCount: formatted });
                    }}
                    placeholder="Contoh: 15.000"
                  />
                  <p className="text-[10px] text-gray-400 ml-4">
                    Tier akan otomatis ditentukan: Nano (0-999) • Micro
                    (1K-9.9K) • Macro (10K-99K) • Mega (100K-999K) • Startup
                    (1M+)
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-10">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                <div className="bg-emerald-50 p-2.5 rounded-xl text-emerald-600">
                  <Banknote className="w-5 h-5" />
                </div>
                <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.25em]">
                  Rekening Pembayaran
                </h4>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    TIPE PEMBAYARAN
                  </label>
                  <select
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-emerald-500 focus:bg-white outline-none font-bold"
                    value={regForm.bankType}
                    onChange={(e) =>
                      setRegForm({
                        ...regForm,
                        bankType: e.target.value as any,
                        bankName: "",
                      })
                    }
                  >
                    <option value="bank">Bank Transfer</option>
                    <option value="e-wallet">E-Wallet</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    {regForm.bankType === "bank"
                      ? "NAMA BANK"
                      : "NAMA E-WALLET"}
                  </label>
                  <select
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-emerald-500 focus:bg-white outline-none font-bold"
                    value={regForm.bankName}
                    onChange={(e) =>
                      setRegForm({ ...regForm, bankName: e.target.value })
                    }
                  >
                    <option value="">
                      Pilih {regForm.bankType === "bank" ? "Bank" : "E-Wallet"}
                      ...
                    </option>
                    {regForm.bankType === "bank" ? (
                      <>
                        <option value="BCA">BCA</option>
                        <option value="Mandiri">Mandiri</option>
                        <option value="BNI">BNI</option>
                        <option value="BRI">BRI</option>
                        <option value="BSI">BSI</option>
                        <option value="CIMB Niaga">CIMB Niaga</option>
                        <option value="Permata">Permata</option>
                        <option value="Danamon">Danamon</option>
                        <option value="BTN">BTN</option>
                        <option value="Mega">Mega</option>
                        <option value="Lainnya">Lainnya</option>
                      </>
                    ) : (
                      <>
                        <option value="DANA">DANA</option>
                        <option value="OVO">OVO</option>
                        <option value="GoPay">GoPay</option>
                        <option value="ShopeePay">ShopeePay</option>
                        <option value="LinkAja">LinkAja</option>
                        <option value="Lainnya">Lainnya</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    {regForm.bankType === "bank"
                      ? "NOMOR REKENING"
                      : "NOMOR E-WALLET"}
                  </label>
                  <input
                    type="text"
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-emerald-500 focus:bg-white outline-none font-mono font-black"
                    value={regForm.bankAccountNumber}
                    onChange={(e) =>
                      setRegForm({
                        ...regForm,
                        bankAccountNumber: e.target.value,
                      })
                    }
                    placeholder={
                      regForm.bankType === "bank"
                        ? "1234567890"
                        : "081234567890"
                    }
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-4">
                    ATAS NAMA
                  </label>
                  <input
                    type="text"
                    className="w-full border-gray-200 border-2 rounded-[2rem] p-5 bg-gray-50/30 text-gray-900 focus:border-emerald-500 focus:bg-white outline-none font-bold uppercase"
                    value={regForm.bankAccountName}
                    onChange={(e) =>
                      setRegForm({
                        ...regForm,
                        bankAccountName: e.target.value,
                      })
                    }
                    placeholder="Nama sesuai rekening/e-wallet"
                  />
                </div>
              </div>
            </div>

            <div className="pt-8 flex justify-end gap-5">
              <button
                type="button"
                onClick={() => setActiveTab("partners")}
                className="px-10 py-5 rounded-[2rem] font-black text-[11px] uppercase text-gray-400"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-14 py-6 bg-slate-900 text-white rounded-[2rem] font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl hover:bg-black transition-all"
              >
                SIMPAN MITRA
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === "vouchers" && (
        <div className="animate-slideUp space-y-6">
          <div className="bg-white rounded-[2rem] shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gray-50/30">
              <div className="flex items-center gap-4">
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cari Voucher (Kode / Nama Mitra)..."
                    className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-giz-primary outline-none transition-all"
                    value={voucherSearchQuery}
                    onChange={(e) => setVoucherSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Tampilkan:
                  </label>
                  <select
                    value={voucherPerPage}
                    onChange={(e) => handleVoucherPerPageChange(Number(e.target.value))}
                    className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-giz-primary outline-none font-bold"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Bulk Actions */}
                {selectedVouchers.length > 0 && (
                  <>
                    <button
                      onClick={handleExportSelectedVouchers}
                      className="bg-blue-50 text-blue-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-blue-100 flex items-center gap-2 transition-all"
                      title="Export data voucher terpilih ke Excel"
                    >
                      <FileSpreadsheet className="w-4 h-4" /> Export Excel ({selectedVouchers.length})
                    </button>
                    <button
                      onClick={handleBulkDeleteVouchers}
                      className="bg-red-50 text-red-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-red-100 flex items-center gap-2 transition-all"
                      title="Hapus voucher terpilih"
                    >
                      <Trash2 className="w-4 h-4" /> Delete ({selectedVouchers.length})
                    </button>
                  </>
                )}
                {/* Import Section */}
                <button
                  onClick={handleDownloadVoucherTemplate}
                  className="bg-emerald-50 text-emerald-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-emerald-100 flex items-center gap-2 transition-all"
                  title="Download Template Excel (berisi daftar Mitra)"
                >
                  <Download className="w-4 h-4" /> Template
                </button>
                <input
                  type="file"
                  ref={voucherFileInputRef}
                  onChange={handleVoucherFileChange}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                <button
                  onClick={() => voucherFileInputRef.current?.click()}
                  disabled={isImporting}
                  className="bg-indigo-50 text-indigo-600 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-indigo-100 flex items-center gap-2 transition-all disabled:opacity-50"
                  title="Import dari Excel"
                >
                  {isImporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Import
                </button>
                <button
                  onClick={() => {
                    setIsVoucherModalOpen(true);
                    setPartnerSearchQuery("");
                    setNewVoucher({
                      code: "",
                      type: "percent",
                      value: 0,
                      creatorId: "",
                    });
                  }}
                  className="bg-gray-900 text-white px-5 py-3 rounded-xl font-black uppercase text-xs flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Buat Voucher
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-gray-400 uppercase text-[9px] tracking-widest border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedVouchers.length === vouchers.length && vouchers.length > 0}
                        onChange={(e) => handleSelectAllVouchers(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-6 py-4">Kode Voucher</th>
                    <th className="px-6 py-4">Diskon Customer</th>
                    <th className="px-6 py-4">Mitra</th>
                    <th className="px-6 py-4">Komisi Mitra</th>
                    <th className="px-6 py-4 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {vouchers.map((v) => (
                    <tr key={v.id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedVouchers.includes(v.id!)}
                          onChange={(e) => handleSelectVoucher(v.id!, e.target.checked)}
                          className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-6 py-4 font-mono font-black text-indigo-600 uppercase flex items-center gap-2">
                        <button
                          type="button"
                          className="hover:bg-indigo-100 text-indigo-600 rounded-lg px-2 py-1 text-[10px] font-black uppercase tracking-widest flex items-center gap-1"
                          onClick={() => {
                            navigator.clipboard.writeText(v.code);
                            setCopiedVouchers(prev => new Set(prev).add(v.id!));
                            setTimeout(() => {
                              setCopiedVouchers(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(v.id!);
                                return newSet;
                              });
                            }, 2000);
                          }}
                        >
                          {copiedVouchers.has(v.id!) ? (
                            <CheckCheck className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        {v.code}
                      </td>
                      <td className="px-6 py-4 text-emerald-600 font-black">
                        {v.type === "percent"
                          ? `${v.value}%`
                          : `Rp ${v.value.toLocaleString("id-ID")}`}
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-700">
                        {v.creatorName}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs bg-emerald-100 px-2 py-1 rounded-full font-black text-emerald-600">
                          {v.partnerCommission}% per transaksi
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEditVoucher(v)}
                            className="text-gray-300 hover:text-amber-500 p-2"
                            title="Edit Voucher"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteVoucher(v.id!)}
                            className="text-gray-300 hover:text-red-500 p-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                meta={{ ...voucherMeta, currentPage: voucherPage }}
                onPageChange={handleVoucherPageChange}
                className="mb-4 p-6"
              />
            </div>
          </div>
        </div>
      )}

      {isVoucherModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn p-4">
          <div className="bg-white p-8 rounded-[2rem] w-full max-w-2xl shadow-2xl relative overflow-y-auto max-h-[90vh]">
            <button
              onClick={() => setIsVoucherModalOpen(false)}
              className="absolute top-6 right-6 text-gray-400 hover:text-red-500"
            >
              <X className="w-6 h-6" />
            </button>
            <h3 className="font-black text-xl mb-1 text-gray-900 uppercase tracking-tight">
              Buat Voucher Baru
            </h3>
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-8">
              Tentukan diskon untuk konsumen (komisi mitra otomatis 6%)
            </p>

            <form onSubmit={handleAddVoucher} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                    Kode Voucher
                  </label>
                  <input
                    autoFocus
                    type="text"
                    placeholder="GIZPROMO"
                    className="w-full border-2 border-gray-100 p-4 rounded-2xl bg-gray-50/30 text-gray-900 font-bold uppercase"
                    value={newVoucher.code}
                    onChange={(e) =>
                      setNewVoucher({ ...newVoucher, code: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                    Pilih Mitra Pemilik
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Cari mitra (nama/email/wa)..."
                      className="w-full border-2 border-gray-100 p-4 rounded-2xl bg-gray-50/30 text-gray-900 font-bold outline-none"
                      value={partnerSearchQuery}
                      onChange={(e) => {
                        setPartnerSearchQuery(e.target.value);
                        setShowPartnerDropdown(true);
                      }}
                      onFocus={() => setShowPartnerDropdown(true)}
                      onBlur={() => setTimeout(() => setShowPartnerDropdown(false), 200)}
                    />
                    {showPartnerDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg max-h-60 overflow-y-auto">
                        {filteredPartners.length > 0 ? (
                          filteredPartners.map((c) => (
                            <div
                              key={c.id}
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                setNewVoucher({ ...newVoucher, creatorId: c.id });
                                setPartnerSearchQuery(c.name);
                                setShowPartnerDropdown(false);
                              }}
                            >
                              <div className="font-bold text-gray-900">{c.name}</div>
                              <div className="text-xs text-gray-500">{c.email}</div>
                              <div className="text-xs text-gray-500">{c.whatsapp}</div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 text-gray-500 text-sm">Tidak ada mitra ditemukan</div>
                        )}
                      </div>
                    )}
                  </div>
                  {newVoucher.creatorId && (
                    <div className="text-xs text-emerald-600 font-bold mt-1">
                      Mitra terpilih: {allPartners.find(p => p.id === newVoucher.creatorId)?.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-emerald-50/50 p-6 rounded-2xl border border-emerald-100 space-y-4">
                <h4 className="text-[10px] font-black text-emerald-700 uppercase tracking-[0.2em] mb-4">
                  Pengaturan Diskon Konsumen
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-400 uppercase">
                      Tipe Diskon
                    </label>
                    <select
                      className="w-full border-2 border-white p-3 rounded-xl font-bold"
                      value={newVoucher.type}
                      onChange={(e) =>
                        setNewVoucher({
                          ...newVoucher,
                          type: e.target.value as any,
                        })
                      }
                    >
                      <option value="percent">Persentase (%)</option>
                      <option value="fixed">Nominal (Rp)</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-400 uppercase">
                      Nilai Diskon
                    </label>
                    <input
                      type="number"
                      className="w-full border-2 border-white p-3 rounded-xl font-bold"
                      value={newVoucher.value}
                      onChange={(e) =>
                        setNewVoucher({
                          ...newVoucher,
                          value: Number(e.target.value),
                        })
                      }
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100">
                <h4 className="text-[10px] font-black text-indigo-700 uppercase tracking-[0.2em] mb-2">
                  Komisi Mitra
                </h4>
                <p className="text-sm text-indigo-600 font-bold">
                  6% dari setiap transaksi yang menggunakan voucher ini
                </p>
                <p className="text-[10px] text-gray-500 mt-2">
                  Komisi akan dihitung otomatis dari total pembayaran customer
                </p>
              </div>

              <button
                type="submit"
                className="w-full py-5 bg-slate-900 text-white font-black uppercase tracking-[0.3em] text-xs rounded-2xl mt-4 shadow-xl hover:bg-black transition-all"
              >
                Simpan Voucher
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Voucher Modal */}
      {isEditVoucherModalOpen && selectedVoucher && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn p-4">
          <div className="bg-white p-8 rounded-[2rem] w-full max-w-2xl shadow-2xl relative overflow-y-auto max-h-[90vh]">
            <button
              onClick={() => {
                setIsEditVoucherModalOpen(false);
                setSelectedVoucher(null);
              }}
              className="absolute top-6 right-6 text-gray-400 hover:text-red-500"
            >
              <X className="w-6 h-6" />
            </button>
            <h3 className="font-black text-xl mb-1 text-gray-900 uppercase tracking-tight">
              Edit Voucher
            </h3>
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-8">
              Update data voucher dan komisi mitra
            </p>

            <form onSubmit={handleUpdateVoucher} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                    Kode Voucher
                  </label>
                  <input
                    autoFocus
                    type="text"
                    placeholder="GIZPROMO"
                    className="w-full border-2 border-gray-100 p-4 rounded-2xl bg-gray-50/30 text-gray-900 font-bold uppercase"
                    value={editVoucher.code}
                    onChange={(e) =>
                      setEditVoucher({ ...editVoucher, code: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                    Pilih Mitra Pemilik
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Cari mitra (nama/email/wa)..."
                      className="w-full border-2 border-gray-100 p-4 rounded-2xl bg-gray-50/30 text-gray-900 font-bold outline-none"
                      value={partnerSearchQuery}
                      onChange={(e) => {
                        setPartnerSearchQuery(e.target.value);
                        setShowPartnerDropdown(true);
                      }}
                      onFocus={() => setShowPartnerDropdown(true)}
                      onBlur={() => setTimeout(() => setShowPartnerDropdown(false), 200)}
                    />
                    {showPartnerDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg max-h-60 overflow-y-auto">
                        {filteredPartners.length > 0 ? (
                          filteredPartners.map((c) => (
                            <div
                              key={c.id}
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                setEditVoucher({ ...editVoucher, creatorId: c.id });
                                setPartnerSearchQuery(c.name);
                                setShowPartnerDropdown(false);
                              }}
                            >
                              <div className="font-bold text-gray-900">{c.name}</div>
                              <div className="text-xs text-gray-500">{c.email}</div>
                              <div className="text-xs text-gray-500">{c.whatsapp}</div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 text-gray-500 text-sm">Tidak ada mitra ditemukan</div>
                        )}
                      </div>
                    )}
                  </div>
                  {editVoucher.creatorId && (
                    <div className="text-xs text-emerald-600 font-bold mt-1">
                      Mitra terpilih: {allPartners.find(p => p.id === editVoucher.creatorId)?.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-emerald-50/50 p-6 rounded-2xl border border-emerald-100 space-y-4">
                <h4 className="text-[10px] font-black text-emerald-700 uppercase tracking-[0.2em] mb-4">
                  Pengaturan Diskon Konsumen
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-400 uppercase">
                      Tipe Diskon
                    </label>
                    <select
                      className="w-full border-2 border-white p-3 rounded-xl font-bold"
                      value={editVoucher.type}
                      onChange={(e) =>
                        setEditVoucher({
                          ...editVoucher,
                          type: e.target.value as any,
                        })
                      }
                    >
                      <option value="percent">Persentase (%)</option>
                      <option value="fixed">Nominal (Rp)</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-400 uppercase">
                      Nilai Diskon
                    </label>
                    <input
                      type="number"
                      className="w-full border-2 border-white p-3 rounded-xl font-bold"
                      value={editVoucher.value}
                      onChange={(e) =>
                        setEditVoucher({
                          ...editVoucher,
                          value: Number(e.target.value),
                        })
                      }
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100 space-y-4">
                <h4 className="text-[10px] font-black text-indigo-700 uppercase tracking-[0.2em] mb-2">
                  Komisi Mitra
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-400 uppercase">
                      Persentase Komisi
                    </label>
                    <input
                      type="number"
                      className="w-full border-2 border-white p-3 rounded-xl font-bold"
                      value={editVoucher.partnerCommission}
                      onChange={(e) =>
                        setEditVoucher({
                          ...editVoucher,
                          partnerCommission: Number(e.target.value),
                        })
                      }
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-indigo-600 font-bold">
                      {editVoucher.partnerCommission}% dari setiap transaksi
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-gray-500 mt-2">
                  Komisi akan dihitung otomatis dari total pembayaran customer
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditVoucherModalOpen(false);
                    setSelectedVoucher(null);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-gray-200"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-amber-500 text-white rounded-xl font-black text-xs uppercase tracking-wider hover:bg-amber-600 transition-all"
                >
                  Update Voucher
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Partner Modal */}
      {isViewModalOpen && selectedPartner && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-2xl shadow-2xl relative overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-8 text-white">
              <button
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedPartner(null);
                }}
                className="absolute top-6 right-6 text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-indigo-500 rounded-2xl flex items-center justify-center text-2xl font-black">
                  {selectedPartner.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-black text-xl uppercase tracking-tight">
                    {selectedPartner.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    <TierBadge tier={selectedPartner.tier} />
                    <span className="text-xs text-white/60 font-bold uppercase">
                      {selectedPartner.platform}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Email
                  </label>
                  <p className="font-bold text-gray-800">
                    {selectedPartner.email || "-"}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    WhatsApp
                  </label>
                  <p className="font-mono font-bold text-emerald-600">
                    {selectedPartner.whatsapp || "-"}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Platform
                  </label>
                  <p className="font-bold text-gray-800 capitalize">
                    {selectedPartner.platform}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Jumlah Followers
                  </label>
                  <p className="font-bold text-indigo-600">
                    {(selectedPartner.followersCount || 0).toLocaleString(
                      "id-ID"
                    )}
                  </p>
                </div>
              </div>

              {selectedPartner.socialMediaUrl && (
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    URL Social Media
                  </label>
                  <a
                    href={selectedPartner.socialMediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-blue-600 hover:underline break-all"
                  >
                    {selectedPartner.socialMediaUrl}
                  </a>
                </div>
              )}

              <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
                <h4 className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-emerald-500" />
                  Informasi Rekening
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-400 uppercase">
                      Bank/E-Wallet
                    </label>
                    <p className="font-bold text-gray-800">
                      {selectedPartner.bankName || "-"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-400 uppercase">
                      Nomor Rekening
                    </label>
                    <p className="font-mono font-bold text-gray-800">
                      {selectedPartner.bankAccountNumber || "-"}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-gray-400 uppercase">
                    Atas Nama
                  </label>
                  <p className="font-bold text-gray-800 uppercase">
                    {selectedPartner.bankAccountName || "-"}
                  </p>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                      Total Komisi Terakumulasi
                    </p>
                    <p className="text-2xl font-black text-emerald-700 mt-1">
                      Rp{" "}
                      {(selectedPartner.totalCommission || 0).toLocaleString(
                        "id-ID"
                      )}
                    </p>
                  </div>
                  <Wallet className="w-10 h-10 text-emerald-300" />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    handleEditPartner(selectedPartner);
                  }}
                  className="px-6 py-3 bg-amber-500 text-white rounded-xl font-black text-xs uppercase tracking-wider hover:bg-amber-600 flex items-center gap-2"
                >
                  <Edit3 className="w-4 h-4" /> Edit Data
                </button>
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedPartner(null);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-gray-200"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Partner Modal */}
      {isEditModalOpen && selectedPartner && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-3xl shadow-2xl relative overflow-y-auto max-h-[90vh]">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white sticky top-0 z-10">
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setSelectedPartner(null);
                }}
                className="absolute top-5 right-5 text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Edit3 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-black text-lg uppercase tracking-tight">
                    Edit Data Mitra
                  </h3>
                  <p className="text-white/80 text-xs font-bold">
                    {selectedPartner.name}
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleUpdatePartner} className="p-8 space-y-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                  <div className="bg-indigo-50 p-2 rounded-lg text-indigo-600">
                    <Users className="w-4 h-4" />
                  </div>
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                    Informasi Profil
                  </h4>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      Nama Lengkap
                    </label>
                    <input
                      required
                      type="text"
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-gray-50/30 text-gray-900 focus:border-amber-500 focus:bg-white outline-none transition-all font-bold"
                      value={editForm.name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      Nomor WhatsApp
                    </label>
                    <input
                      type="tel"
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-gray-50/30 text-gray-900 focus:border-amber-500 focus:bg-white outline-none transition-all font-bold"
                      value={editForm.whatsapp}
                      onChange={(e) => handleEditWhatsappChange(e.target.value)}
                      placeholder="628xxx"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                    Email
                  </label>
                  <input
                    required
                    type="email"
                    className="w-full border-gray-200 border-2 rounded-xl p-4 bg-gray-50/30 text-gray-900 focus:border-amber-500 focus:bg-white outline-none transition-all font-bold"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      Platform Social Media
                    </label>
                    <select
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-white text-gray-900 focus:border-amber-500 outline-none font-bold"
                      value={editForm.platformType}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          platformType: e.target.value as any,
                        })
                      }
                    >
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="youtube">YouTube</option>
                      <option value="blog">Blog / Website</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      Jumlah Followers
                    </label>
                    <input
                      type="text"
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-gray-50/30 text-gray-900 focus:border-amber-500 focus:bg-white outline-none transition-all font-bold"
                      value={editForm.followersCount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        const formatted = val
                          ? parseInt(val, 10).toLocaleString("id-ID")
                          : "";
                        setEditForm({ ...editForm, followersCount: formatted });
                      }}
                      placeholder="Contoh: 15.000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                    URL Social Media
                  </label>
                  <input
                    type="url"
                    className="w-full border-gray-200 border-2 rounded-xl p-4 bg-gray-50/30 text-gray-900 focus:border-amber-500 focus:bg-white outline-none transition-all font-bold"
                    value={editForm.socialMediaUrl}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        socialMediaUrl: e.target.value,
                      })
                    }
                    placeholder={
                      editForm.platformType === "instagram"
                        ? "https://instagram.com/username"
                        : editForm.platformType === "tiktok"
                        ? "https://tiktok.com/@username"
                        : editForm.platformType === "youtube"
                        ? "https://youtube.com/@channel"
                        : "https://yourblog.com"
                    }
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                  <div className="bg-emerald-50 p-2 rounded-lg text-emerald-600">
                    <Banknote className="w-4 h-4" />
                  </div>
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                    Rekening Pembayaran
                  </h4>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      Tipe Pembayaran
                    </label>
                    <select
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-white text-gray-900 focus:border-emerald-500 outline-none font-bold"
                      value={editForm.bankType}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          bankType: e.target.value as any,
                          bankName: "",
                        })
                      }
                    >
                      <option value="bank">Bank Transfer</option>
                      <option value="e-wallet">E-Wallet</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      {editForm.bankType === "bank"
                        ? "Nama Bank"
                        : "Nama E-Wallet"}
                    </label>
                    <select
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-white text-gray-900 focus:border-emerald-500 outline-none font-bold"
                      value={editForm.bankName}
                      onChange={(e) =>
                        setEditForm({ ...editForm, bankName: e.target.value })
                      }
                    >
                      <option value="">
                        Pilih{" "}
                        {editForm.bankType === "bank" ? "Bank" : "E-Wallet"}...
                      </option>
                      {editForm.bankType === "bank" ? (
                        <>
                          <option value="BCA">BCA</option>
                          <option value="Mandiri">Mandiri</option>
                          <option value="BNI">BNI</option>
                          <option value="BRI">BRI</option>
                          <option value="BSI">BSI</option>
                          <option value="CIMB Niaga">CIMB Niaga</option>
                          <option value="Permata">Permata</option>
                          <option value="Danamon">Danamon</option>
                          <option value="BTN">BTN</option>
                          <option value="Mega">Mega</option>
                          <option value="Lainnya">Lainnya</option>
                        </>
                      ) : (
                        <>
                          <option value="DANA">DANA</option>
                          <option value="OVO">OVO</option>
                          <option value="GoPay">GoPay</option>
                          <option value="ShopeePay">ShopeePay</option>
                          <option value="LinkAja">LinkAja</option>
                          <option value="Lainnya">Lainnya</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      {editForm.bankType === "bank"
                        ? "Nomor Rekening"
                        : "Nomor E-Wallet"}
                    </label>
                    <input
                      type="text"
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-gray-50/30 text-gray-900 focus:border-emerald-500 focus:bg-white outline-none font-mono font-bold"
                      value={editForm.bankAccountNumber}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          bankAccountNumber: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                      Atas Nama
                    </label>
                    <input
                      type="text"
                      className="w-full border-gray-200 border-2 rounded-xl p-4 bg-gray-50/30 text-gray-900 focus:border-emerald-500 focus:bg-white outline-none font-bold uppercase"
                      value={editForm.bankAccountName}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          bankAccountName: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                  <div className="bg-purple-50 p-2 rounded-lg text-purple-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                    Status Mitra
                  </h4>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                    Status Aktivitas
                  </label>
                  <select
                    className="w-full border-gray-200 border-2 rounded-xl p-4 bg-white text-gray-900 focus:border-purple-500 outline-none font-bold"
                    value={editForm.status}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        status: e.target.value as any,
                      })
                    }
                  >
                    <option value="active">Aktif</option>
                    <option value="inactive">Tidak Aktif</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedPartner(null);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-gray-200"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="px-8 py-3 bg-amber-500 text-white rounded-xl font-black text-xs uppercase tracking-wider hover:bg-amber-600 flex items-center gap-2 disabled:opacity-50"
                >
                  {isUpdating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : null}
                  Simpan Perubahan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
